import React, {FC, useCallback, useEffect, useMemo, useState} from 'react';

import {atIndex} from '../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {PromiseCompletionSource} from '../../../../libs/shared/helpers/src/utilities/asyncHelpers';
import {
  ApplicationConversation,
  ApplicationSingleConversation,
  ExistingConversationBaseContext,
  MultiConversationsContext,
  NoConversationContext,
  SingleConversationContext,
} from '../../../applications/bridge/v2/types/bridgeTypesV2';
import {
  ConversationActionContextFinder,
  ConversationInboxIdsFinder,
  ConversationNamespaceFinder,
} from '../../../helpers/models/conversationHelpers';
import {useAppDispatch, useAppSelector} from '../../../helpers/reduxHelpers';
import {InboxModel} from '../../../models/api/inboxModel';
import {
  LegacyConversationLinkDropdownEntryPointModel,
  LegacySidePanelEntryPointModel,
} from '../../../models/api/legacyEntryPointModel';
import {buildPublicId, tryParsePublicId} from '../../../models/applications/publicIdTypes';
import {applicationListConversationCommentsAction} from '../../../store/data/applications/applicationCommentsActions';
import {
  applicationAssignConversationsAction,
  applicationMoveConversationsAction,
  applicationSetConversationsStatusAsyncAction,
  applicationTagConversationsAsyncAction,
  applicationUntagConversationsAsyncAction,
} from '../../../store/data/applications/applicationConversationsActions';
import {applicationListConversationRecipientsAction} from '../../../store/data/applications/applicationRecipientsActions';
import {applicationDownloadAttachmentAsyncAction} from '../../../store/data/applications/applicationsAttachmentActions';
import {
  applicationComposeMessageAsyncAction,
  applicationComposeReplyAction,
  applicationFetchDraftAction,
  applicationListConversationsMessagesAction,
} from '../../../store/data/applications/applicationsMessagesActions';
import {
  addLegacyApplicationTopicToConversationsAsyncAction,
  removeLegacyApplicationLinkFromConversationAsyncAction,
} from '../../../store/data/applications/legacyApplicationsTopicsActions';
import {isInboxUnreachableToTrackersAction} from '../../../store/data/conversations/moveConversationsActions';
import {fetchInboxByIdAsyncAction} from '../../../store/data/inboxes/inboxesActions';
import {getHasNewMoveToUnreachable} from '../../../store/featureFlags/featureFlagsSelectors';
import {ViewerPanelId} from '../../../store/panels/panelStateBase';
import {MoveAccessDialog} from '../../inboxes/viewer/conversation/topbar/move/moveAccessDialogContainer';
import {buildBaseContext, LegacyContextBuilder} from './legacyApplicationContextBase';
import {LegacyApplicationContextHostContainer} from './legacyApplicationContextHostContainer';

/*
 * Props.
 */

interface LegacyConversationsApplicationContextHostProps {
  panelId: ViewerPanelId;
  entryPoint: LegacyConversationLinkDropdownEntryPointModel | LegacySidePanelEntryPointModel;
  conversationIds: ReadonlyArray<number>;
  singleConversation: ApplicationSingleConversation | undefined;
  conversations: ReadonlyArray<ApplicationConversation>;
  findConversationActionContext: ConversationActionContextFinder;
  findConversationNamespace: ConversationNamespaceFinder;
  findConversationInboxIds: ConversationInboxIdsFinder;
  loadedViewerConversationId: number | undefined;
}

/*
 * Component.
 */

interface DestinationInboxState {
  inbox: InboxModel;
  isUnreachable: boolean;
  isUserOnlyFollower: boolean;
}

export const LegacyConversationsApplicationContextHost: FC<LegacyConversationsApplicationContextHostProps> = (
  props,
) => {
  /*
   * Provide a promise that indicates whether the conversation is loaded.
   */

  const {conversationIds, loadedViewerConversationId} = props;
  const singleConversationId = conversationIds.length === 1 ? atIndex(conversationIds, 0) : undefined;
  const hasNewMoveToUnreachable = useAppSelector(getHasNewMoveToUnreachable);
  const [destinationInboxState, setDestinationInboxState] = useState<DestinationInboxState>();

  // Create a promise source that always matches the requested conversation.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadPromiseSource = useMemo(() => new PromiseCompletionSource(), [singleConversationId]);

  // Whenever the promise changes, cancel the previous one.
  useEffect(
    () => () => {
      loadPromiseSource.promise.catch(() => {}); // Reject throws if nothing catches.
      loadPromiseSource.reject(new Error('Conversation changed.'));
    },
    [loadPromiseSource],
  );

  // When the loaded conversation matches the requested conversation, resolve the promise.
  useEffect(() => {
    if (loadedViewerConversationId !== singleConversationId) {
      return;
    }

    loadPromiseSource.resolve(undefined);
  }, [loadedViewerConversationId, singleConversationId, loadPromiseSource]);

  /*
   * Build the context.
   */

  const {
    panelId,
    entryPoint,
    singleConversation,
    conversations,
    findConversationActionContext,
    findConversationNamespace,
    findConversationInboxIds,
  } = props;
  const {applicationId} = entryPoint;

  const contextBuilder: LegacyContextBuilder = useCallback(
    (parameters) => {
      const baseContext = buildBaseContext(parameters);
      const {dispatch} = parameters;

      // No conversations.
      if (!singleConversation && conversations.length === 0) {
        const noConversationContext: NoConversationContext = {
          ...baseContext,
          type: 'noConversation',
        };
        return noConversationContext;
      }

      // One or more conversations, common properties.
      const conversationBaseContext: ExistingConversationBaseContext = {
        ...baseContext,

        /** @deprecated */
        addTopic: (externalUrl, name) =>
          dispatch(
            addLegacyApplicationTopicToConversationsAsyncAction(applicationId, conversationIds, {
              externalUrl,
              name,
            }),
          ),

        addLink: (externalUrl, name) =>
          dispatch(
            addLegacyApplicationTopicToConversationsAsyncAction(applicationId, conversationIds, {
              externalUrl,
              name,
            }),
          ),

        assign: async (teammateId) => {
          dispatch(
            applicationAssignConversationsAction(conversationIds, teammateId, findConversationInboxIds),
          );
        },

        move: async (inboxId) => {
          const privateInboxId = tryParsePublicId(inboxId);
          if (!privateInboxId) {
            return;
          }

          const unreachableState = dispatch(
            isInboxUnreachableToTrackersAction(conversationIds, privateInboxId),
          );

          // If the inbox is reachable OR we don't have the TvI FF, carry on like normal.
          if (!unreachableState.isUnreachable || !hasNewMoveToUnreachable) {
            dispatch(
              applicationMoveConversationsAction(
                conversationIds,
                inboxId,
                findConversationNamespace,
                undefined,
              ),
            );
            return;
          }

          const unreachableInbox = await dispatch(fetchInboxByIdAsyncAction(privateInboxId));

          if (!unreachableInbox) {
            return;
          }

          // Otherwise, store our data to render the modal
          setDestinationInboxState({
            inbox: unreachableInbox,
            isUnreachable: unreachableState.isUnreachable,
            isUserOnlyFollower: unreachableState.isUserOnlyFollower,
          });
        },

        setStatus: async (status) => {
          dispatch(
            applicationSetConversationsStatusAsyncAction(
              panelId,
              conversationIds,
              status,
              findConversationActionContext,
            ),
          );
        },

        tag: async (tagIds) => {
          await dispatch(applicationTagConversationsAsyncAction(conversationIds, tagIds));
        },

        untag: async (tagIds) => {
          await dispatch(applicationUntagConversationsAsyncAction(conversationIds, tagIds));
        },

        removeLink: async (linkId) => {
          await dispatch(
            removeLegacyApplicationLinkFromConversationAsyncAction(applicationId, linkId, conversationIds),
          );
        },
      };

      // One conversation.
      if (singleConversation) {
        const [conversationId] = conversationIds;

        const singleConversationContext: SingleConversationContext = {
          ...conversationBaseContext,
          type: 'singleConversation',
          conversation: singleConversation,

          listMessages: async () => {
            // We need the conversation to be loaded to return the messages.
            await loadPromiseSource.promise;
            return dispatch(applicationListConversationsMessagesAction(panelId, conversationId));
          },

          listComments: async () => {
            // We need the conversation to be loaded to return the comments.
            await loadPromiseSource.promise;
            return dispatch(applicationListConversationCommentsAction(panelId, conversationId));
          },

          downloadAttachment: async (messageOrCommentId: string, attachmentId: string) => {
            // We need the conversation to be loaded before we can fetch message attachment data.
            await loadPromiseSource.promise;
            return dispatch(
              applicationDownloadAttachmentAsyncAction(
                panelId,
                conversationId,
                messageOrCommentId,
                attachmentId,
              ),
            );
          },

          fetchDraft: async (id) => dispatch(applicationFetchDraftAction(conversationId, id)),

          createDraft: async (draftTemplate) => {
            if (!draftTemplate.replyOptions) {
              return dispatch(applicationComposeMessageAsyncAction(draftTemplate));
            }

            // We need the conversation to be loaded before we can reply to a message.
            await loadPromiseSource.promise;

            return dispatch(applicationComposeReplyAction(panelId, conversationId, draftTemplate));
          },

          listRecipients: async () => {
            // We need the conversation to be loaded to return the recipients.
            await loadPromiseSource.promise;
            return dispatch(applicationListConversationRecipientsAction(panelId, conversationId));
          },
        };
        return singleConversationContext;
      }

      // Two or more conversations.
      const multiConversationsContext: MultiConversationsContext = {
        ...conversationBaseContext,
        type: 'multiConversations',
        conversations,
      };
      return multiConversationsContext;
    },
    [
      singleConversation,
      conversations,
      applicationId,
      conversationIds,
      hasNewMoveToUnreachable,
      panelId,
      findConversationActionContext,
      findConversationNamespace,
      loadPromiseSource.promise,
    ],
  );

  /*
   * Special case for move actions.
   */

  const appDispatch = useAppDispatch();

  const maybeRenderUnreachableDialog = () => {
    if (!hasNewMoveToUnreachable || !destinationInboxState) {
      return null;
    }

    return (
      <MoveAccessDialog
        isUserOnlyFollower={destinationInboxState.isUserOnlyFollower}
        destinationInbox={destinationInboxState.inbox}
        onRequestClose={() => {
          setDestinationInboxState(undefined);
        }}
        conversationsCount={conversationIds.length}
        onKeepAccessClick={() => {
          appDispatch(
            applicationMoveConversationsAction(
              conversationIds,
              buildPublicId('inbox', destinationInboxState.inbox.id),
              props.findConversationNamespace,
              true,
            ),
          );
        }}
        onLoseAccessClick={() => {
          appDispatch(
            applicationMoveConversationsAction(
              conversationIds,
              buildPublicId('inbox', destinationInboxState.inbox.id),
              props.findConversationNamespace,
              false,
            ),
          );
        }}
      />
    );
  };

  /*
   * Use the base context host with our extra context props.
   */

  return (
    <>
      <LegacyApplicationContextHostContainer
        panelId={panelId}
        entryPoint={entryPoint}
        contextBuilder={contextBuilder}
      />
      {maybeRenderUnreachableDialog()}
    </>
  );
};
